// src/components/rpa/agent/ManagementReportCard.js
import React, { useState } from 'react';
import axios from 'axios';
import './ManagementReportCard.css';

const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

const ManagementReportCard = ({ docId, docName, clientName, initialReport }) => {
  const [report,      setReport]      = useState(initialReport || '');
  const [generating,  setGenerating]  = useState(!initialReport);
  const [error,       setError]       = useState('');
  const [copied,      setCopied]      = useState(false);

  // Auto-generate on mount if not pre-loaded
  React.useEffect(() => {
    if (!initialReport) fetchReport();
  }, []);

  const fetchReport = async () => {
    setGenerating(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${API_URL}/admin/rpa/agent/management-report`,
        { docId },
        { withCredentials: true }
      );
      if (data.ok) setReport(data.report);
      else setError(data.error || data.message || 'Report generation failed.');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(report);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([report], { type: 'text/plain' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `Management_Letter_${(clientName || 'Client').replace(/\s+/g, '_')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Parse sections from the letter for enhanced rendering
  const parseSections = (text) => {
    if (!text) return [];
    // Split on numbered section headers like "1. Scope" or "Section 1:"
    const lines = text.split('\n');
    const sections = [];
    let currentSection = null;

    for (const line of lines) {
      const isHeader = /^(\d+\.\s+\*?\*?[A-Z]|\*\*\d+\.|\#{1,3}\s)/.test(line.trim()) ||
                       /^[A-Z][A-Z\s&]+:$/.test(line.trim());
      if (isHeader) {
        if (currentSection) sections.push(currentSection);
        currentSection = { heading: line.trim().replace(/\*\*/g, ''), body: [] };
      } else {
        if (currentSection) currentSection.body.push(line);
        else sections.push({ heading: null, body: [line] });
      }
    }
    if (currentSection) sections.push(currentSection);
    return sections;
  };

  const sections = parseSections(report);
  const useParsed = sections.length > 3;

  return (
    <div className="mgmt-card">
      <div className="mgmt-header">
        <span className="mgmt-icon">📄</span>
        <div>
          <div className="mgmt-title">Management Communication Letter</div>
          <div className="mgmt-sub">{clientName || docName}</div>
        </div>
        <span className="mgmt-badge">The Joachim Group</span>
      </div>

      {generating && (
        <div className="mgmt-generating">
          <div className="mgmt-spinner" />
          <span>Drafting management letter from audit findings… this may take a moment.</span>
        </div>
      )}

      {error && !generating && (
        <div className="mgmt-error">
          <div>{error}</div>
          <button className="mgmt-retry-btn" onClick={fetchReport}>Retry</button>
        </div>
      )}

      {report && !generating && (
        <>
          <div className="mgmt-actions">
            <button className="mgmt-action-btn copy" onClick={handleCopy}>
              {copied ? '✓ Copied' : '📋 Copy'}
            </button>
            <button className="mgmt-action-btn download" onClick={handleDownload}>
              ⬇ Download
            </button>
            <button className="mgmt-action-btn regenerate" onClick={fetchReport}>
              ↻ Regenerate
            </button>
          </div>

          {useParsed ? (
            <div className="mgmt-sections">
              {sections.map((s, i) => (
                <div key={i} className="mgmt-section">
                  {s.heading && <div className="mgmt-section-heading">{s.heading}</div>}
                  <div className="mgmt-section-body">
                    {s.body.join('\n').trim()}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <pre className="mgmt-report-text">{report}</pre>
          )}
        </>
      )}
    </div>
  );
};

export default ManagementReportCard;